import React, { useState, useCallback, useEffect, useRef } from "react"
import { uploadToS3 } from '../utils/uploadToS3';
import { api, getAuthHeaders } from '../utils/api';
import Layout from "../components/Layout"
import { Formik, Form, ErrorMessage } from "formik"
import * as Yup from "yup"
import Select from "react-select"
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone"
import { showSuccessToast, showErrorToast } from "../components/Toast";
import Loader from "../components/Loader";
import "../styles/assets-liability.scss"
import { useUser } from "../redux/useUser";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocation } from "react-router-dom";
import SetReminderSection from "../components/liability-forms/common/SetReminderSection";
import {
  fetchRemindersForLiability,
  createReminder,
  updateReminder,
  deleteReminder
} from "../utils/api";
import { formatLocalDate } from "../utils/dateUtils";

// AI Extraction utilities for liability forms
import {
  normalizeLiabilityFrequency,
  normalizeRepaymentFrequency,
  normalizeTaxType,
  normalizeLoanType,
  normalizeVehicleLoanType,
  normalizeBNPLProvider,
  normalizeInsuranceType,
  normalizeDebtCategory,
  normalizeLiabilityExtractedItem,
  getExtractedLiabilityArray,
  fillLiabilityExtractedFields,
  extractAndSetCurrency,
  getCurrencyFromSymbol,
  detectCurrencyFromAmount,
  LIABILITY_PICKER_CONFIG
} from "../utils/liabilityExtractionUtils";


// Import form components
import MortgageForm, { loanFormValidationSchema } from "../components/liability-forms/mortgage-form.js"
import VehicleForm, { vehicleValidationSchema } from "../components/liability-forms/vehicle-loan-form.js"
import LoanForm, { cashValidationSchema } from "../components/liability-forms/loan-form.js"
import CreditCardForm, { creditCardValidationSchema } from "../components/liability-forms/credit-card-form.js"
import OverdraftForm, { overdraftValidationSchema } from "../components/liability-forms/overdraft-form.js"
import TaxPayableForm, { taxValidationSchema} from "../components/liability-forms/tax-payable-form.js"
import BNPLForm, {bnplValidationSchema} from "../components/liability-forms/buy-now-pay-later-form.js"
import InsurancePremiumForm, {insuranceValidationSchema }from "../components/liability-forms/insurance-premium-form.js"
import PersonalDebtsForm, {liabilityValidationSchema} from "../components/liability-forms/personal-debts-form.js"

// Liability options
const liabilityOptions = [
  { value: "mortgage", label: "Mortgage" },
  { value: "vehicle loan", label: "Vehicle Loan" },
  { value: "loan", label: "Loan" },
  { value: "credit card", label: "Credit Card" },
  { value: "overdraft", label: "Overdraft" },
  { value: "tax payable", label: "Tax Payable" },
  { value: "bnpl", label: "Buy Now Pay Later" },
  { value: "insurance premium", label: "Insurance Premium" },
  { value: "personal debts", label: "Personal Debt" },
]

// Validation schema
const addLiabilitySchema = Yup.object().shape({
  liability: Yup.string().required("Liability is required"),
  notes: Yup.string()
    .max(500, "Notes must be less than 500 characters")
    .test(
      "valid-notes",
      "Notes contain invalid or unsafe content.",
      value => {
        if (!value) return true;
        // Disallow script, iframe, img, inline JS, SQL patterns
        const forbiddenPatterns = [
          /<script.*?>/i,
          /<iframe.*?>/i,
          /<img.*?onerror=.*?>/i,
          /on\w+\s*=\s*['"].*?['"]/i, // inline JS events
          /(['"]).*?\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE|EXEC|UNION|--|;|\bOR\b|\bAND\b).*?\1/i, // SQLi
        ];
        for (const pattern of forbiddenPatterns) {
          if (pattern.test(value)) return false;
        }
        // Allow printable chars, common punctuation, symbols, newlines, URLs
        // Disallow control chars except \n, \r, \t
        const printable = /^[\x20-\x7E\n\r\t]*$/;
        if (!printable.test(value)) return false;
        return true;
      }
    ),
});

// Reminder types come back from the API in lowercase; the reminder form uses "Weekly" / "Monthly" / "Yearly".
const normalizeReminderType = (type) =>
  ({ weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" })[String(type || "").toLowerCase()] || "Monthly";

// Helper to merge schemas
function mergeSchemas(base, extra) {
  if (!extra) return base;
  return base.concat(extra);
}

// Helper function to get validation schema for selected liability type
function getValidationSchema(liabilityType) {
  switch (liabilityType) {
    case "mortgage":
      return mergeSchemas(addLiabilitySchema, loanFormValidationSchema);
    case "vehicle loan":
      return mergeSchemas(addLiabilitySchema, vehicleValidationSchema);
    case "loan":
      return mergeSchemas(addLiabilitySchema, cashValidationSchema);
    case "credit card":
      return mergeSchemas(addLiabilitySchema, creditCardValidationSchema);
    case "overdraft":
      return mergeSchemas(addLiabilitySchema, overdraftValidationSchema);
    case "tax payable":
      return mergeSchemas(addLiabilitySchema, taxValidationSchema);
    case "insurance premium":
      return mergeSchemas(addLiabilitySchema, insuranceValidationSchema);
    case "personal debts":
      return mergeSchemas(addLiabilitySchema, liabilityValidationSchema);
    case "bnpl":
      return mergeSchemas(addLiabilitySchema, bnplValidationSchema);
    default:
      return addLiabilitySchema;
  }
}

const AddLiability = ({ initialValues: apiInitialValues, liabilityId, onSuccess, onSubmit: parentOnSubmit, enterEditMode, readOnly: readOnlyProp }) => {

  const { user } = useUser();
  const navigate = useNavigate();

  // Track selected liability for dynamic initial values
  const [selectedLiability, setSelectedLiability] = useState((apiInitialValues && apiInitialValues.liability) || "mortgage");

  // states for uploaded files/docs/tags — if editing, populate from apiInitialValues
  const [uploadedDocuments, setUploadedDocuments] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [fileUploading, setFileUploading] = useState(false);
  const [fileProgresses, setFileProgresses] = useState({});
  const [fileErrorMessages, setFileErrorMessages] = useState({});
  const [docInputKey, setDocInputKey] = useState(0);
  const docInputRef = useRef(null);
  const [tags, setTags] = useState([])
  const [newTag, setNewTag] = useState("")
  const [isSaved, setIsSaved] = useState(false)
  const [currentStep, setCurrentStep] = useState(liabilityId ? 2 : 1)
  const isMobile = useIsMobile()
  const location = useLocation();

  // Reminder states
  const [reminders, setReminders] = useState([]);
  const [showReminderSection, setShowReminderSection] = useState("no");
  const [remindersCollapsed, setRemindersCollapsed] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    description: "",
    type: "Monthly",
    dayOfMonth: "1",
    monthOfYear: "1",
  });

  // AI Extraction states
  const [extractLoading, setExtractLoading] = useState(false);
  const [extractMessage, setExtractMessage] = useState("");
  const setFieldValueRef = useRef(null);
  const [extractedItems, setExtractedItems] = useState([]);
  const [showPickerModal, setShowPickerModal] = useState(false);
  const [fullExtractedData, setFullExtractedData] = useState(null);

  const [localReadOnly, setLocalReadOnly] = useState(() => {
    // If readOnlyProp is provided from parent, use it
    if (readOnlyProp !== undefined) {
      return readOnlyProp;
    }
    // Otherwise, check location state
    return location.state?.edit === true
    ? false
    : location.state?.edit === false
    ? true
    : false;
  });

  // Sync localReadOnly with readOnlyProp from parent when it changes
  useEffect(() => {
    if (readOnlyProp !== undefined) {
      setLocalReadOnly(readOnlyProp);
    }
  }, [readOnlyProp]);


function normalizeLiability(raw) {
  if (!raw) return "mortgage";
  const s = String(raw).trim().toLowerCase();

  // exact map table (most explicit first)
  const map = {
    "creditcard": "credit card",
    "credit_card": "credit card",
    "credit card": "credit card",
    "credit-card": "credit card",
    "credit": "credit card",
    "personaldebt": "personal debts",
    "personal debt": "personal debts",
    "personal-debt": "personal debts",
    "personaldebts": "personal debts",
    "bnpl": "bnpl",
    "buy now pay later": "bnpl",
    "insurancepremium": "insurance premium",
    "insurance premium": "insurance premium",
    "taxpayable": "tax payable",
    "tax payable": "tax payable",
    "overdraft": "overdraft",
    "mortgage": "mortgage",
    "vehicleloan": "vehicle loan",
    "vehicle loan": "vehicle loan",
    "vehicle": "vehicle loan",
    "loan": "loan",
    "generalloan": "loan",
    "general loan": "loan",
  };

  // try direct map
  if (map[s]) return map[s];

  // remove non-alphanumeric and try again
  const cleaned = s.replace(/[^a-z0-9]/g, "");
  if (map[cleaned]) return map[cleaned];

  // fallback heuristics (safety orders)
  if (cleaned.includes("credit")) return "credit card";
  if (cleaned.includes("mortg")) return "mortgage";
  if (cleaned.includes("vehicle") || cleaned.includes("car")) return "vehicle loan";
  if (cleaned.includes("bnpl") || cleaned.includes("buynow")) return "bnpl";
  if (cleaned.includes("insurance") || cleaned.includes("premium")) return "insurance premium";
  if (cleaned.includes("tax")) return "tax payable";
  if (cleaned.includes("overdraft")) return "overdraft";
  if (cleaned.includes("personal") || cleaned.includes("debt")) return "personal debts";
  if (cleaned.includes("loan")) return "loan";

  return "mortgage";
}



  // Helper to get initial values for selected liability type
  function getDynamicInitialValues(liability) {
    try {
      switch (liability) {
        case "mortgage":
          return require("../components/liability-forms/mortgage-form.js").loanFormInitialValues;
        case "vehicle loan":
          return require("../components/liability-forms/vehicle-loan-form.js").vehicleInitialValues;
        case "loan":
          return require("../components/liability-forms/loan-form.js").cashInitialValues;
        case "credit card":
          return require("../components/liability-forms/credit-card-form.js").creditCardInitialValues;
        case "overdraft":
          return require("../components/liability-forms/overdraft-form.js").overdraftInitialValues;
        case "tax payable":
          return require("../components/liability-forms/tax-payable-form.js").taxInitialValues;
        case "insurance premium":
          return require("../components/liability-forms/insurance-premium-form.js").insuranceInitialValues;
        case "personal debts":
          return require("../components/liability-forms/personal-debts-form.js").liabilityInitialValues;
        case "bnpl":
          return require("../components/liability-forms/buy-now-pay-later-form.js").bnplInitialValues;
        default:
          return {};
      }
    } catch (e) {
      return {};
    }
  }
function buildTouchedFromInitial(obj, prefix = "") {
  const touched = {};
  if (obj === null || obj === undefined) return touched;

  // treat primitives / dates as leaf nodes
  if (typeof obj !== "object" || obj instanceof Date) {
    if (prefix) touched[prefix] = true;
    return touched;
  }

  // arrays -> use index notation
  if (Array.isArray(obj)) {
    // if array is empty, still mark the array path itself as touched so Field-level checks can show
    if (!obj.length && prefix) touched[prefix] = true;
    obj.forEach((item, idx) => {
      const path = prefix ? `${prefix}[${idx}]` : `${idx}`;
      Object.assign(touched, buildTouchedFromInitial(item, path));
    });
    return touched;
  }

  // plain object -> traverse keys
  Object.keys(obj).forEach((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    Object.assign(touched, buildTouchedFromInitial(obj[key], path));
  });

  // if object had no keys (empty object) mark the object path itself
  if (!Object.keys(obj).length && prefix) touched[prefix] = true;

  return touched;
}
  // Populate local file/tag states if apiInitialValues exists (edit mode)
 useEffect(() => {
  if (!apiInitialValues) return;
  const apiLiabilityRaw = apiInitialValues.liability || apiInitialValues.liabilityType || apiInitialValues.type || apiInitialValues.category;
  const normalized = normalizeLiability(apiLiabilityRaw);
  setSelectedLiability(normalized);

  // tags
  if (Array.isArray(apiInitialValues.tags)) {
    setTags(apiInitialValues.tags.slice());
  } else if (typeof apiInitialValues.tags === "string" && apiInitialValues.tags.length) {
    // optional: if tags come as comma-separated string
    setTags(apiInitialValues.tags.split(",").map(t => t.trim()).filter(Boolean));
  }

  // files/documents (if backend returns files/images/photos structure)
  // Load only photos into uploadedFiles - check photos first, then images
  const apiFiles = apiInitialValues.photos || apiInitialValues.images || apiInitialValues.files || [];
  const files = Array.isArray(apiFiles)
    ? apiFiles.map((f, idx) => ({
        id: `existing-file-${idx}`,
        uploadedUrl: f.url || f,
        name: f.filename || f.originalName || `file-${idx}`,
        type: f.mimetype || (f.url ? (f.url.match(/\.(\w+)$/) ? `.${f.url.split('.').pop()}` : "") : ""),
      }))
    : [];
  setUploadedFiles(files.filter(f => f.uploadedUrl));

  // Load documents separately - only from documents key
  const apiDocs = apiInitialValues.documents || [];
  const docs = Array.isArray(apiDocs)
    ? apiDocs.map((d, idx) => ({
        id: `existing-doc-${idx}`,
        uploadedUrl: d.url || d,
        name: d.filename || d.originalName || `doc-${idx}`,
        type: d.mimetype || d.type || (d.url ? (d.url.match(/\./) ? d.url.split('.').pop().toLowerCase() : "") : ""),
      }))
    : [];
  setUploadedDocuments(docs.filter(d => d.uploadedUrl));
}, [apiInitialValues]);

  // Fetch reminders in edit mode
  useEffect(() => {
    if (liabilityId && user?.id) {
      fetchRemindersForLiability(user.id, liabilityId)
        .then((response) => {
          const fetchedReminders = response?.results || response?.data?.results || response?.data || [];
          if (Array.isArray(fetchedReminders)) {
            const mappedReminders = fetchedReminders.map((r) => ({
              id: r._id || r.id,
              description: r.description || "",
              // The API stores lowercase types ("monthly") and yearly days under `day`; the form uses
              // "Monthly"/"Yearly" and string days. Without this the edit form showed "Day undefined"
              // and saved the reminder without its day (ML-17, ML-18).
              type: normalizeReminderType(r.recurrencePattern?.type),
              dayOfMonth: String(r.recurrencePattern?.dayOfMonth ?? r.recurrencePattern?.day ?? "1"),
              monthOfYear: String(r.recurrencePattern?.month ?? "1"),
            }));
            setReminders(mappedReminders);
            if (mappedReminders.length > 0) {
              setShowReminderSection("yes");
              // Sync into Formik without triggering a full reinitialize
              if (setFieldValueRef.current) {
                setFieldValueRef.current("setReminderToggle", "yes");
              }
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch reminders:", err);
        });
    }
  }, [liabilityId, user]);

  // Auto-fill extracted data after liability type switch
  useEffect(() => {
    if (extractedItems && extractedItems.length > 0 && !showPickerModal && setFieldValueRef.current) {
      if (extractedItems.length === 1) {
        const timer = setTimeout(() => {
          const mergedItem = { ...fullExtractedData, ...extractedItems[0] };
          const warnings = fillLiabilityExtractedFields(mergedItem, setFieldValueRef.current, selectedLiability);

          if (Object.keys(warnings).length > 0) {
            const warningMsg = Object.entries(warnings)
              .map(([field, msg]) => `⚠️ ${field}: ${msg}`)
              .join('\n');
            setExtractMessage(`✅ ${selectedLiability} details auto-filled.\n\n${warningMsg}`);
          } else {
            setExtractMessage(`✅ ${selectedLiability} details auto-filled from document.`);
          }

          setExtractedItems([]);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [extractedItems, showPickerModal, selectedLiability]);

  // Document dropzone - now uses AI extraction
  const onDocumentDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        uploadDocumentAndExtract(file, setFieldValueRef.current);
      });
    },
    []
  );

  const {
    getRootProps: getDocRootProps,
    getInputProps: getDocInputProps,
    isDragActive: isDocDragActive,
    open: openDocInput,
  } = useDropzone({
    onDrop: onDocumentDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxSize: 10 * 1024 * 1024, // 10 MB
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  // Photo/Files dropzone
  const onFilesDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }))
    setUploadedFiles((prev) => [...prev, ...newFiles])
    acceptedFiles.forEach((file) => showSuccessToast(`File added successfully!`));
  }, [])

  const {
    getRootProps: getFilesRootProps,
    getInputProps: getFilesInputProps,
    isDragActive: isFilesDragActive,
  } = useDropzone({
    onDrop: onFilesDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    multiple: true,
  })

  // Remove uploaded document
  const removeDocument = (id) => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== id))
  }

  // Remove uploaded file
  const removeFile = (id) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id))
  }

  // Add tag
  const addTag = () => {
    const trimmedTag = newTag.trim();
    if (!trimmedTag) {
      showErrorToast("Tag cannot be empty.");
      return;
    }
    if (trimmedTag.length < 1) {
      showErrorToast("Tag must be at least 1 character.");
      return;
    }
    if (trimmedTag.length > 30) {
      showErrorToast("Tag cannot exceed 30 characters.");
      return;
    }
    // Tags are compared without case ("home" = "HOME"); clear the box so the next tag starts fresh (ML-06).
    if (tags.some((tag) => tag.toLowerCase() === trimmedTag.toLowerCase())) {
      showErrorToast("Duplicate tag not allowed.");
      setNewTag("");
      return;
    }
    if (tags.length >= 10) {
      showErrorToast("Maximum 10 tags allowed.");
      return;
    }
    setTags((prev) => [...prev, trimmedTag]);
    setNewTag("");
  }

  // Remove tag
  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove))
  }

  // Handle tag input
  const handleTagInput = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag()
    }
  }

  // Helper function to get month name
  const getMonthName = (monthNum) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[parseInt(monthNum) - 1] || "January";
  };

  // Helper function to calculate next reminder date
  const calculateNextReminderDate = (type, dayOfMonth, monthOfYear) => {
    const now = new Date();
    let nextDate = new Date();

    if (type === "Weekly") {
      // Set to next week same day
      nextDate.setDate(now.getDate() + 7);
    } else if (type === "Monthly") {
      // Set to specific day of current/next month
      const day = parseInt(dayOfMonth);
      nextDate.setDate(day);

      // If the day has already passed this month, move to next month
      if (nextDate <= now) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
    } else if (type === "Yearly") {
      // Set to specific month and day of current/next year
      const month = parseInt(monthOfYear) - 1; // JS months are 0-indexed
      const day = parseInt(dayOfMonth);

      nextDate.setMonth(month);
      nextDate.setDate(day);

      // If the date has already passed this year, move to next year
      if (nextDate <= now) {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
    }

    return nextDate.toISOString();
  };

  // Handle Add/Update Reminder
  const handleAddReminder = async () => {
    if (!reminderForm.description.trim()) {
      showErrorToast("Please enter a reminder description");
      return;
    }

    const newReminder = {
      description: reminderForm.description,
      type: reminderForm.type,
      dayOfMonth: reminderForm.type === "Monthly" || reminderForm.type === "Yearly" ? reminderForm.dayOfMonth : undefined,
      monthOfYear: reminderForm.type === "Yearly" ? reminderForm.monthOfYear : undefined,
    };

    if (editingReminderId) {
      // Edit mode with liabilityId - update via API
      if (liabilityId) {
        try {
          const reminderPayload = {
            userId: user.id,
            liabilityId: liabilityId,
            referenceType: "liability",
            type: "other",
            date: calculateNextReminderDate(
              newReminder.type,
              newReminder.dayOfMonth,
              newReminder.monthOfYear
            ),
            description: newReminder.description,
            recurrencePattern: {
              type: newReminder.type.toLowerCase(),
              dayOfMonth: newReminder.dayOfMonth ? parseInt(newReminder.dayOfMonth) : undefined,
              month: newReminder.monthOfYear ? parseInt(newReminder.monthOfYear) : undefined,
              day: newReminder.type === "Yearly" ? parseInt(newReminder.dayOfMonth) : undefined,
            },
          };

          await updateReminder(editingReminderId, reminderPayload);

          setReminders((prev) =>
            prev.map((r) =>
              r.id === editingReminderId ? { ...newReminder, id: editingReminderId } : r
            )
          );
          showSuccessToast("Reminder updated successfully");
        } catch (err) {
          console.error("Failed to update reminder:", err);
          showErrorToast("Failed to update reminder");
          return;
        }
      } else {
        // Add mode - update local state
        setReminders((prev) =>
          prev.map((r) =>
            r.id === editingReminderId ? { ...newReminder, id: editingReminderId } : r
          )
        );
      }
      setEditingReminderId(null);
    } else {
      // Add new reminder
      if (liabilityId) {
        // Edit mode with liabilityId - create via API
        try {
          const reminderPayload = {
            userId: user.id,
            liabilityId: liabilityId,
            referenceType: "liability",
            type: "other",
            date: calculateNextReminderDate(
              newReminder.type,
              newReminder.dayOfMonth,
              newReminder.monthOfYear
            ),
            description: newReminder.description,
            recurrencePattern: {
              type: newReminder.type.toLowerCase(),
              dayOfMonth: newReminder.dayOfMonth ? parseInt(newReminder.dayOfMonth) : undefined,
              month: newReminder.monthOfYear ? parseInt(newReminder.monthOfYear) : undefined,
              day: newReminder.type === "Yearly" ? parseInt(newReminder.dayOfMonth) : undefined,
            },
          };

          const response = await createReminder(reminderPayload);
          const createdReminderId = response?.data?._id || response?._id || Date.now().toString();

          setReminders((prev) => [...prev, { ...newReminder, id: createdReminderId }]);
          showSuccessToast("Reminder added successfully");
          setEditingReminderId('temp');
          setRemindersCollapsed(false);
          setTimeout(() => setEditingReminderId(null), 0);
        } catch (err) {
          console.error("Failed to create reminder:", err);
          showErrorToast("Failed to create reminder");
          return;
        }
      } else {
        // Add mode - add to local state
        const tempId = Date.now().toString();
        setReminders((prev) => [...prev, { ...newReminder, id: tempId }]);
        setEditingReminderId('temp');
        setRemindersCollapsed(false);
        setTimeout(() => setEditingReminderId(null), 0);
      }
    }

    // Reset form
    setReminderForm({
      description: "",
      type: "Monthly",
      dayOfMonth: "1",
      monthOfYear: "1",
    });
  };

  // Handle Edit Reminder
  const handleEditReminder = (reminder) => {
    setReminderForm({
      description: reminder.description,
      type: normalizeReminderType(reminder.type),
      dayOfMonth: reminder.dayOfMonth || "1",
      monthOfYear: reminder.monthOfYear || "1",
    });
    setEditingReminderId(reminder.id);
  };

  // Handle Delete Reminder
  const handleDeleteReminder = async (reminderId) => {
    if (liabilityId) {
      // Edit mode - delete via API
      try {
        await deleteReminder(reminderId);
        setReminders((prev) => prev.filter((r) => r.id !== reminderId));
        showSuccessToast("Reminder deleted successfully");
      } catch (err) {
        console.error("Failed to delete reminder:", err);
        showErrorToast("Failed to delete reminder");
      }
    } else {
      // Add mode - delete from local state
      setReminders((prev) => prev.filter((r) => r.id !== reminderId));
    }

    if (editingReminderId === reminderId) {
      setEditingReminderId(null);
      setReminderForm({
        description: "",
        type: "Monthly",
        dayOfMonth: "1",
        monthOfYear: "1",
      });
    }
  };

  // Handle Complete Reminder (placeholder - can be implemented if needed)
  const handleCompleteReminder = async (reminderId) => {
    console.log("Complete reminder:", reminderId);
  };

  // upload helper (single-file)
  const uploadFileToS3 = async (fileObj) => {
    try {
      const s3Response = await uploadToS3(fileObj.file, {
        user,
        setImageUploading: setFileUploading,
        setProgresses: setFileProgresses,
        showSuccessToast: () => {},
        showErrorToast,
        setErrorMessages: setFileErrorMessages,
        updateReduxUser: false,
      });
      let fileUrl = s3Response;
      if (s3Response && typeof s3Response === 'object') {
        fileUrl = s3Response.fileName || s3Response.url;
      }
      setUploadedFiles((prev) => {
        return prev.map((f) => f.id === fileObj.id ? { ...f, uploadedUrl: fileUrl, type: fileObj.type } : f);
      });
    } catch (error) {
      // Error handling is done in utility
    }
  };

  // Upload document and extract AI data
  // Upload document to S3 and extract AI data
  const uploadDocumentAndExtract = useCallback(async (file, setFieldValue) => {
    if (!file) return null;

    const fileObj = {
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    };

    // Optimistic add to documents
    setUploadedDocuments((prev) => [...prev, fileObj]);

    setExtractLoading(true);
    setExtractMessage("");

    // Upload to S3 and call AI extraction
    uploadToS3(file, {
      user,
      setImageUploading: setFileUploading,
      setProgresses: setFileProgresses,
      showSuccessToast: () => {},
      showErrorToast,
      setErrorMessages: setFileErrorMessages,
      updateReduxUser: false,
    })
      .then(async (s3Resp) => {
        let s3Url = null;
        if (s3Resp) {
          if (typeof s3Resp === 'string') s3Url = s3Resp;
          else if (typeof s3Resp === 'object') s3Url = s3Resp.url || s3Resp.fileName || s3Resp.location || null;
        }

        // Update document with uploaded URL
        setUploadedDocuments((prev) =>
          prev.map((d) => (d.id === fileObj.id ? { ...d, uploadedUrl: s3Url } : d))
        );

        if (s3Url) {
          // Call AI extraction API
          const payload = { s3Url, type: "liability" };
          const extractRes = await api.post("/ai-extraction/extract", payload, {
            headers: getAuthHeaders(),
          });
          showSuccessToast("Document uploaded and sent for AI extraction");

          const extractedData = extractRes?.data?.data?.data || {};
          const extractedLiabilityType = extractedData.liabilityType;
          const purchaseCurrency = extractedData.purchaseCurrency;

          // Normalize for comparison
          const normalize = (str) => {
            if (!str) return "";
            let normalized = str.toLowerCase().replace(/\s+/g, "");
            if (normalized === "bankaccounts") normalized = "bankaccount";
            normalized = normalized.replace(/s$/, "");
            return normalized;
          };

          // Clear documents after processing
          setUploadedDocuments([]);

          // AUTO-SWITCHING: Check if extracted liability type matches selected
          if (extractedLiabilityType && normalize(extractedLiabilityType) !== normalize(selectedLiability)) {
            setExtractMessage(`📄 Document is for ${extractedLiabilityType}. Switching form...`);

            // Find detected array
            const detectedKeys = [
              `${extractedLiabilityType}Details`,
              `${normalize(extractedLiabilityType)}Details`,
              extractedLiabilityType,
              normalize(extractedLiabilityType)
            ];
            let detectedArr = null;

            for (const key of detectedKeys) {
              if (Array.isArray(extractedData[key])) {
                detectedArr = extractedData[key];
                break;
              }
              if (extractedData[key] && typeof extractedData[key] === 'object') {
                detectedArr = [extractedData[key]];
                break;
              }
            }

            // Fallback
            if (!detectedArr || detectedArr.length === 0) {
              const normalized = normalizeLiabilityExtractedItem(extractRes?.data, extractedLiabilityType);
              if (Object.keys(normalized).length > 0) {
                detectedArr = [normalized];
              }
            }

            // Store extracted data
            if (detectedArr && detectedArr.length > 0) {
              setExtractedItems(detectedArr);
              setFullExtractedData(extractedData);
              if (detectedArr.length > 1) {
                setShowPickerModal(true);
              }
            }

            // Auto-switch liability type
            const normalizedExtractedType = normalize(extractedLiabilityType);
            const liabilityMapping = {
              'mortgage': 'mortgage',
              'vehicle': 'vehicle loan',
              'vehicleloan': 'vehicle loan',
              'loan': 'loan',
              'generalloan': 'loan',
              'creditcard': 'credit card',
              'credit': 'credit card',
              'overdraft': 'overdraft',
              'tax': 'tax payable',
              'taxpayable': 'tax payable',
              'bnpl': 'bnpl',
              'buynowpaylater': 'bnpl',
              'insurance': 'insurance premium',
              'insurancepremium': 'insurance premium',
              'personaldbt': 'personal debts',
              'personal': 'personal debts'
            };

            const detectedLiability = liabilityMapping[normalizedExtractedType] || extractedLiabilityType;
            setSelectedLiability(detectedLiability);
            
            // Auto-detect currency for the switched liability type
            let autoDetectedCurrency = purchaseCurrency;
            if (!autoDetectedCurrency) {
              const amountFields = ['originalLoanAmount', 'outstandingAmount', 'currentBalance', 'totalAmount', 'premiumAmount', 'monthlyRepayment', 'amountDue'];
              for (const field of amountFields) {
                const fieldValue = extractedData[field];
                if (fieldValue && typeof fieldValue === 'string') {
                  autoDetectedCurrency = detectCurrencyFromAmount(fieldValue);
                  if (autoDetectedCurrency) break;
                }
              }
            }
            
            // Default to INR if no currency detected
            const switchedCurrency = autoDetectedCurrency || purchaseCurrency || 'INR';
            
            // Store detected currency for later use after form reinitializes
            if (setFieldValue) {
              // Delay setting currency until after form reinitializes
              setTimeout(() => {
                setFieldValue("currency", switchedCurrency);
                setFieldValue("currencyCode", switchedCurrency);
              }, 100);
            }
            
            return;
          }

          // Liability type matches - proceed with auto-fill
          let extractedArr = getExtractedLiabilityArray(extractedData, selectedLiability);

          // Fallback
          if (!extractedArr || extractedArr.length === 0) {
            const normalized = normalizeLiabilityExtractedItem(extractRes?.data, selectedLiability);
            if (Object.keys(normalized).length > 0) {
              extractedArr = [normalized];
            }
          }

          // Set currency if present
          let detectedCurrency = purchaseCurrency;
          
          // If no purchaseCurrency, try to detect from amount fields
          if (!detectedCurrency) {
            const amountFields = ['originalLoanAmount', 'outstandingAmount', 'currentBalance', 'totalAmount', 'premiumAmount', 'monthlyRepayment', 'amountDue'];
            for (const field of amountFields) {
              const fieldValue = extractedData[field];
              if (fieldValue && typeof fieldValue === 'string') {
                detectedCurrency = detectCurrencyFromAmount(fieldValue);
                if (detectedCurrency) break;
              }
            }
          }
          
          // If no currency detected, default to INR
          const finalCurrency = detectedCurrency || purchaseCurrency || 'INR';
          
          // Set currency in the form
          if (setFieldValue) {
            setFieldValue("currency", finalCurrency);
            setFieldValue("currencyCode", finalCurrency);
          }

          if (extractedLiabilityType && normalize(extractedLiabilityType) === normalize(selectedLiability)) {
            if (Array.isArray(extractedArr) && extractedArr.length > 0) {
              // Single record - auto fill
              if (extractedArr.length === 1) {
                const mergedItem = { ...extractedData, ...extractedArr[0] };
                const warnings = fillLiabilityExtractedFields(mergedItem, setFieldValue, selectedLiability);

                if (Object.keys(warnings).length > 0) {
                  const warningMsg = Object.entries(warnings)
                    .map(([field, msg]) => `⚠️ ${field}: ${msg}`)
                    .join('\n');
                  setExtractMessage(`✅ ${selectedLiability} details auto-filled.\n\n${warningMsg}`);
                } else {
                  setExtractMessage(`✅ ${selectedLiability} details auto-filled from document.`);
                }
              }
              // Multiple records - show picker
              else {
                setExtractedItems(extractedArr);
                setFullExtractedData(extractedData);
                setShowPickerModal(true);
                setExtractMessage(`Multiple ${selectedLiability} records found. Please select one.`);
              }
            }
          } else if (!extractedLiabilityType) {
            setExtractMessage("Could not detect liability type from document.");
          }
        } else {
          showErrorToast("Upload completed but no URL returned");
        }
      })
      .catch((err) => {
        console.error("uploadDocumentAndExtract error", err);
        const apiMsg = err?.response?.data?.message || err?.message || "Failed to upload document for AI extraction";
        showErrorToast(apiMsg);
        setExtractMessage(apiMsg);
        setUploadedDocuments((prev) => prev.filter((d) => d.id !== fileObj.id));
      })
      .finally(() => {
        setExtractLoading(false);
      });

    return null;
  }, [user, extractedItems, showPickerModal, selectedLiability]);

  const handleUploadAllFiles = async () => {
    for (const fileObj of uploadedFiles) {
      if (!fileObj.uploadedUrl) {
        await uploadFileToS3(fileObj);
      }
    }
  };

  const allFilesUploaded = uploadedFiles.length === 0 || uploadedFiles.every(f => f.uploadedUrl);

  // --- Helper: build common files payload (handles uploadedFiles + uploadedDocuments) ---
  const buildCommonFilesPayload = async () => {
    const uploadedFileInfos = [];

    // helper to upload/process file
    const processFile = async (fileObj, isDoc = false) => {
      let fileUrl = fileObj.uploadedUrl || null;
      let fileName = fileObj.name;

      if (!fileUrl && fileObj.file) {
        try {
          const s3Response = await uploadToS3(fileObj.file, {
            user,
            setImageUploading: setFileUploading,
            setProgresses: setFileProgresses,
            showSuccessToast: () => {},
            showErrorToast,
            setErrorMessages: setFileErrorMessages,
            updateReduxUser: false,
          });

          if (s3Response && typeof s3Response === "object") {
            fileUrl = s3Response.url || s3Response.fileName || fileUrl;
            fileName = s3Response.fileName || fileName;
          } else if (typeof s3Response === "string") {
            fileUrl = s3Response;
          }
        } catch (err) {
          console.error("S3 upload failed for file:", fileObj.name, err);
        }

        // update state
        if (!isDoc) {
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === fileObj.id ? { ...f, uploadedUrl: fileUrl, type: fileObj.type } : f))
          );
        } else {
          setUploadedDocuments((prev) =>
            prev.map((d) => (d.id === fileObj.id ? { ...d, uploadedUrl: fileUrl, type: fileObj.type } : d))
          );
        }
      }

      return {
        url: fileUrl,
        fileCategory: "other",
        size: fileObj.size > 0 ? fileObj.size : 1,
        mimetype: fileObj.type || "",
        originalName: fileObj.name || fileName,
        filename: fileName,
      };
    };

    // sequentially process uploadedFiles
    for (const fileObj of uploadedFiles) {
      const meta = await processFile(fileObj, false);
      uploadedFileInfos.push(meta);
    }

    // process uploadedDocuments (if any)
    for (const docObj of uploadedDocuments) {
      const meta = await processFile(docObj, true);
      uploadedFileInfos.push(meta);
    }

    // partition into images vs documents
    const images = uploadedFileInfos.filter((f) => f.url && f.mimetype && f.mimetype.startsWith("image/"));
    const documents = uploadedFileInfos.filter((f) => f.url && !(f.mimetype && f.mimetype.startsWith("image/")));

    return { documents, photos: images, files: uploadedFileInfos, tags };
  };

  // Handle save as draft
  // Save as Draft: only require 'name', skip all other validations
  // This function should be called directly from the button, not as a Formik submit handler
  const handleSaveAsDraft = async (values) => {
    // Check if name is provided - support both 'name' and 'liabilityName' fields
    const nameValue = values.name || values.liabilityName;
    if (!nameValue || nameValue.trim() === "") {
      showErrorToast("Name is required to save as draft.");
      return;
    }
    
    // Call internalSubmit with isDraft = true
    await internalSubmit(values, null, true);
  };

  // Handle form submission
  const internalSubmit = async (values, formikHelpers, isDraft = false) => {
    // Handle case where formikHelpers is null (draft save)
    if (formikHelpers) {
      formikHelpers.setSubmitting(true);
    }
    setIsSaved(true);
    
    // CAPTURE REMINDER DATA AT THE START - before any async operations
    // This ensures we have the correct state at submission time (before any state resets)
    // Match AddAssets pattern: check values.setReminder === "yes"
    console.log("🔍 Capturing reminder data from form values:");
    console.log("  - values.setReminderToggle:", values.setReminderToggle);
    console.log("  - values.reminderDescription:", values.reminderDescription);
    
    const shouldCreateReminders = values.setReminderToggle === "yes";
    const remindersToCreate = shouldCreateReminders ? [{
      description: values.reminderDescription || '',
      type: values.reminderType || "Monthly",
      dayOfMonth: values.reminderDay || "1",
      monthOfYear: values.reminderMonth || "1"
    }] : [];
    
    console.log("  - shouldCreateReminders:", shouldCreateReminders, "| remindersToCreate:", remindersToCreate);
    
    // Build common files payload (will upload missing files)
    const { documents, photos } = await buildCommonFilesPayload();

    // Build liability specific body (only specific fields) — same as your existing mapping
    let specific = null;
    switch (values.liability) {
      case "bnpl":
        specific = {
          name: values.name || "MacBook Pro Purchase",
          liabilityCategory: "unsecured",
          liabilityType: "bnpl",
          originalAmount: Number(values.originalAmount || values.totalAmount || 0),
          outstandingAmount: Number(values.outstandingAmount || values.remainingBalance || 0),
          currency: values.currency || "USD",
          interestRate: 0,
          startDate: values.startDate ? formatLocalDate(values.startDate) : null,
          endDate: values.endDate ? formatLocalDate(values.endDate) : null,
          nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
          description: values.description || "4-payment plan for MacBook Pro",
          notes: values.notes || "",
          bnplDetails: {
            merchantName: values.merchantName || "Apple Store",
            serviceProvider: values.serviceProvider || "Klarna",
            purchaseDescription: values.purchaseDescription || "MacBook Pro 14-inch M3 Pro chip",
            totalAmount: Number(values.totalAmount || 0),
            remainingBalance: Number(values.remainingBalance || 0),
            nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
            nextPaymentAmount: Number(values.nextPaymentAmount || 0),
            remainingPayments: Number(values.remainingPayments || 0),
            interestFees: Number(values.interestFees || 0),
            paymentSchedule: values.paymentSchedule || "",
          },
        };
        break;
      case "personal debts":
        if (isDraft) {
          // For draft mode, only include the name field - other fields should be blank
          specific = {
            name: values.liabilityName || values.name,
            liabilityCategory: "unsecured",
            liabilityType: "personalDebt",
            originalAmount: values.originalAmount ? Number(values.originalAmount) : 0,
            outstandingAmount: values.outstandingAmount ? Number(values.outstandingAmount) : 0,
            currency: values.currency || user?.defaultCurrency || user?.currency || null,
            interestRate: values.interestRate ? Number(values.interestRate) : null,
            status: values.status || null,
            startDate: values.createdDate ? formatLocalDate(values.createdDate) : null,
            endDate: values.lastUpdated ? formatLocalDate(values.lastUpdated) : null,
            nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
            description: values.description || null,
            notes: values.notes || null,
            personalDebtDetails: {
              category: values.category || null,
            },
          };
        } else {
          // For non-draft mode, include all fields with defaults
          specific = {
            name: values.liabilityName || values.name || "Loan from Mom",
            liabilityCategory: "unsecured",
            liabilityType: "personalDebt",
            originalAmount: Number(values.originalAmount || 0),
            outstandingAmount: Number(values.outstandingAmount || 0),
            currency: values.currency || user?.defaultCurrency || user?.currency || "USD",
            interestRate: values.interestRate ? Number(values.interestRate) : 0,
            status: values.status || "active",
            startDate: values.createdDate ? (formatLocalDate(values.createdDate) || values.createdAt) : null,
            endDate: values.lastUpdated ? formatLocalDate(values.lastUpdated) : null,
            nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
            description: values.description || "Personal loan from family for emergency expenses",
            notes: values.notes || "",
            personalDebtDetails: {
              category: values.category || "family",
            },
          };
        }
        break;
      case "insurance premium":
        specific = {
          name: values.name || "Health Insurance Premium",
          liabilityCategory: "unsecured",
          liabilityType: "insurancePremium",
          originalAmount: Number(values.originalAmount || values.premiumAmount || 0),
          outstandingAmount: Number(values.outstandingAmount || values.premiumAmount || 0),
          currency: values.currency || "USD",
          interestRate: 0,
          startDate: values.policyStartDate ? formatLocalDate(values.policyStartDate) : (values.startDate ? formatLocalDate(values.startDate) : null),
          endDate: values.policyEndDate ? formatLocalDate(values.policyEndDate) : (values.endDate ? formatLocalDate(values.endDate) : null),
          nextPaymentDate: values.dueDate ? formatLocalDate(values.dueDate) : (values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null),
          description: values.description || "Monthly health insurance premium payments",
          notes: values.notes || "",
          insurancePremiumDetails: {
            insuranceType: values.insuranceType || "health",
            insuranceCompany: values.insuranceCompany || "Blue Cross Blue Shield",
            policyNumber: values.policyNumber || "BCBS-2025-789456",
            premiumAmount: Number(values.premiumAmount || 0),
            repaymentFrequency: values.repaymentFrequency || "monthly",
            dueDate: values.dueDate ? formatLocalDate(values.dueDate) : null,
            policyStartDate: values.policyStartDate ? formatLocalDate(values.policyStartDate) : null,
            policyEndDate: values.policyEndDate ? formatLocalDate(values.policyEndDate) : null,
            deductibleAmount: Number(values.deductibleAmount || 0),
          },
        };
        break;
      case "tax payable":
        specific = {
          name: values.name || "Quarterly Tax Payment Q3 2025",
          liabilityCategory: "unsecured",
          liabilityType: "taxPayable",
          originalAmount: Number(values.amountDue || values.originalAmount || 0),
          outstandingAmount: Number(values.amountDue || values.outstandingAmount || 0),
          currency: values.currency || "USD",
          interestRate: values.interestCharges ? Number(values.interestCharges) : 0,
          startDate: values.dueDate ? formatLocalDate(values.dueDate) : null,
          nextPaymentDate: values.dueDate ? formatLocalDate(values.dueDate) : null,
          description: values.description || "Quarterly estimated tax payment for Q3 2025",
          notes: values.notes || "",
          taxPayableDetails: {
            taxType: values.taxType || "income",
            taxYearPeriod: values.taxYearPeriod || "2025 Q3",
            amountDue: Number(values.amountDue || 0),
            dueDate: values.dueDate ? formatLocalDate(values.dueDate) : null,
            penalty: Number(values.penalty || 0),
            interestCharges: Number(values.interestCharges || 0),
            paymentStatus: values.paymentStatus || "pending",
          },
        };
        break;
      case "overdraft":
        specific = {
          name: values.name || "Checking Account Overdraft",
          liabilityCategory: "unsecured",
          liabilityType: "overdraft",
          originalAmount: Number(values.overdraftLimit || 0),
          outstandingAmount: Number(values.currentOverdraftAmount || 0),
          currency: values.currency || values.currency || "USD",
          interestRate: values.interestRate ? Number(values.interestRate) : null,
          startDate: values.overdraftStartDate ? formatLocalDate(values.overdraftStartDate) : null,
          nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
          description: values.description || "Temporary overdraft on checking account",
          notes: values.notes || "",
          overdraftDetails: {
            bankName: values.bankName || "Wells Fargo",
            accountName: values.accountName || "Primary Checking Account",
            overdraftLimit: Number(values.overdraftLimit || 0),
            overdraftStartDate: values.overdraftStartDate ? formatLocalDate(values.overdraftStartDate) : null,
            currentOverdraftAmount: Number(values.currentOverdraftAmount || 0),
            overdraftFee: Number(values.overdraftFee || 0),
            repaymentFrequency: values.repaymentFrequency || "monthly",
          },
        };
        break;
      case "credit card":
        specific = {
          name: values.name || "Chase Sapphire Preferred",
          liabilityCategory: "unsecured",
          liabilityType: "creditCard",
          originalAmount: Number(values.originalAmount || 0),
          outstandingAmount: Number(values.currentBalance || 0),
          currency: values.currency || null,
          interestRate: values.interestRate ? Number(values.interestRate) : null,
          startDate: values.startDate ? formatLocalDate(values.startDate) : null,
          nextPaymentDate: values.paymentDueDate ? formatLocalDate(values.paymentDueDate) : null,
          description: values.description || "Rewards credit card for travel and dining",
          notes: values.notes || "",
          creditCardDetails: {
            cardName: values.cardName || "Chase Sapphire Preferred",
            issuer: values.issuer || "JPMorgan Chase Bank",
            creditLimit: Number(values.creditLimit || 0),
            currentBalance: Number(values.currentBalance || 0),
            annualPercentageRate: values.interestRate ? Number(values.interestRate) : null,
            minimumPayment: Number(values.minimumPayment || 0),
            repaymentFrequency: values.repaymentFrequency || "monthly",
            statementDate: values.statementDate ? formatLocalDate(values.statementDate) : null,
            paymentDueDate: values.paymentDueDate ? formatLocalDate(values.paymentDueDate) : null,
          },
        };
        break;
      case "loan":
        specific = {
          name: values.name || "Personal Consolidation Loan",
          liabilityCategory: "unsecured",
          liabilityType: "generalLoan",
          originalAmount: Number(values.originalAmount || 0),
          outstandingAmount: Number(values.outstandingAmount || 0),
          currency: values.currency || null,
          interestRate: values.interestRate ? Number(values.interestRate) : null,
          startDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
          endDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
          description: values.description || "Personal loan for debt consolidation",
          notes: values.notes || "",
          generalLoanDetails: {
            loanType: values.loanType || "personal",
            loanPurpose: values.loanPurpose || "",
            lenderName: values.lenderName || "",
            originalAmount: Number(values.originalAmount || 0),
            loanStartDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
            loanTermMonths: values.loanTerm ? Number(values.loanTerm) : null,
            repaymentFrequency: values.repaymentFrequency || "",
            nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
            maturityDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          },
        };
        break;
      case "mortgage":
        specific = {
          name: values.name || "Primary Home Mortgage",
          liabilityCategory: "secured",
          liabilityType: "mortgage",
          originalAmount: Number(values.originalLoanAmount || 0),
          outstandingAmount: Number(values.outstandingAmount || 0),
          currency: values.originalLoanCurrency || null,
          interestRate: values.interestRate ? Number(values.interestRate) : null,
          startDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
          endDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
          description: values.description || "30-year fixed mortgage for primary residence",
          notes: values.notes || "",
          mortgageDetails: {
            propertyAddress: values.propertyAddress || "",
            lenderName: values.lenderName || "",
            loanType: (values.loanType || "fixed").toLowerCase(),
            originalLoanAmount: Number(values.originalLoanAmount || 0),
            // originalLoanCurrency: values.originalLoanCurrency || null,
            loanStartDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
            loanTermYears: values.loanTerm ? Number(values.loanTerm) : null,
            monthlyRepayment: values.monthlyRepayment ? Number(values.monthlyRepayment) : null,
            repaymentType: (values.repaymentType || "principalAndInterest").replace(/\s+/g, ""),
            offsetAccountLinked: values.offsetAccountLinked === "Yes",
            nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
            maturityDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          },
        };
        break;
      case "vehicle loan":
        specific = {
          name: values.name || "Vehicle Loan",
          liabilityCategory: "secured",
          liabilityType: "vehicleLoan",
          originalAmount: Number(values.originalLoanAmount || 0),
          outstandingAmount: Number(values.outstandingAmount || 0),
          currency: values.currency || null,
          interestRate: values.interestRate ? Number(values.interestRate) : null,
          startDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
          endDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          nextPaymentDate: values.nextPaymentDate ? formatLocalDate(values.nextPaymentDate) : null,
          description: values.description || "Vehicle loan for car purchase",
          notes: values.notes || "",
          vehicleLoanDetails: {
            vehicleType: values.vehicleType || "car",
            vehicleName: values.vehicleName || "",
            makeModel: values.makeModel || "",
            registrationNumber: values.registrationNumber || "",
            ownershipType: values.ownershipType || "",
            lenderName: values.lenderName || "",
            originalLoanAmount: Number(values.originalLoanAmount || 0),
            loanStartDate: values.loanStartDate ? formatLocalDate(values.loanStartDate) : null,
            loanTermMonths: values.loanTerm ? Number(values.loanTerm) : null,
            repaymentFrequency: values.repaymentFrequency || "",
            paymentDueDate: values.paymentDueDate ? formatLocalDate(values.paymentDueDate) : null,
            maturityDate: values.maturityDate ? formatLocalDate(values.maturityDate) : null,
          },
        };
        break;
      default:
        if (formikHelpers) {
          formikHelpers.setSubmitting(false);
        }
        showErrorToast("Please select a valid liability type");
        return;
    }

    // Merge common part in one place
    const payload = {
      ...specific,
      documents,
      photos,
      tags,
      isDraft,
    };

    // Remove empty string values from payload to avoid API validation errors
    Object.keys(payload).forEach(key => {
      if (payload[key] === "" || payload[key] === null) {
        delete payload[key];
      }
    });

    // If parent provided an onSubmit handler, use it (edit flow). Otherwise, fallback to POST (create flow)
// inside internalSubmit, replace the delegation / API try-catch with this:

try {
  // If parent provided a submit handler, always delegate to it.
  if (typeof parentOnSubmit === "function") {
    const newLiabilityId = await parentOnSubmit(payload, formikHelpers);

    // Create reminders on new liability creation
    if (newLiabilityId && shouldCreateReminders && remindersToCreate.length > 0) {
      for (const reminder of remindersToCreate) {
        if (!reminder?.description?.trim()) continue;
        const reminderPayload = {
          userId: user.id,
          liabilityId: newLiabilityId,
          referenceType: "liability",
          type: "other",
          date: calculateNextReminderDate(reminder.type, reminder.dayOfMonth, reminder.monthOfYear),
          description: reminder.description,
          recurrencePattern:
            reminder.type === "Weekly"
              ? { type: "weekly" }
              : reminder.type === "Monthly"
              ? { type: "monthly", dayOfMonth: Number.parseInt(reminder.dayOfMonth) }
              : { type: "yearly", month: Number.parseInt(reminder.monthOfYear), day: Number.parseInt(reminder.dayOfMonth) },
        };
        try {
          await createReminder(reminderPayload);
        } catch (reminderError) {
          console.error("Failed to create reminder:", reminderError);
          showErrorToast("Liability created but failed to set reminder");
        }
      }
    }

    // Delete all existing reminders when user switches to No in edit mode
    if (!newLiabilityId && !shouldCreateReminders && reminders.length > 0) {
      for (const reminder of reminders) {
        try {
          await deleteReminder(reminder.id);
        } catch (reminderError) {
          console.error("Failed to delete reminder:", reminderError);
        }
      }
    }

    if (typeof onSuccess === "function") onSuccess();
    return;
  }

  // Fallback: perform API call locally if parent did not provide handler
  const { api, getAuthHeaders } = await import("../utils/api");
  const token = localStorage.getItem("access_token");
  const headers = getAuthHeaders ? getAuthHeaders(token) : {};

  let res;
  if (liabilityId) {
    res = await api.put(`/liabilities/${liabilityId}`, payload, { headers });
    showSuccessToast(isDraft ? "Draft saved successfully!" : "Liability updated successfully!");
  } else {
    try {
      res = await api.post("/liabilities", payload, { headers });
    } catch (apiError) {
      console.error("❌ Liability API call failed:", apiError);
      throw apiError;
    }

    const newlyCreatedLiabilityId = res?.data?._id || res?.data?.id;

    if (!newlyCreatedLiabilityId) {
      throw new Error("Failed to get liability ID from response");
    }

    if (shouldCreateReminders && remindersToCreate.length > 0) {
      for (const reminder of remindersToCreate) {
        if (!reminder?.description?.trim()) continue;
        const reminderPayload = {
          userId: user.id,
          liabilityId: newlyCreatedLiabilityId,
          referenceType: "liability",
          type: "other",
          date: calculateNextReminderDate(reminder.type, reminder.dayOfMonth, reminder.monthOfYear),
          description: reminder.description,
          recurrencePattern:
            reminder.type === "Weekly"
              ? { type: "weekly" }
              : reminder.type === "Monthly"
              ? { type: "monthly", dayOfMonth: Number.parseInt(reminder.dayOfMonth) }
              : { type: "yearly", month: Number.parseInt(reminder.monthOfYear), day: Number.parseInt(reminder.dayOfMonth) },
        };
        try {
          await createReminder(reminderPayload);
        } catch (reminderError) {
          console.error("Failed to create reminder:", reminderError);
          showErrorToast("Liability created but failed to set reminder");
        }
      }
    }

    showSuccessToast(isDraft ? "Draft saved successfully!" : "Liability added successfully!");
  }

  // CRITICAL: Only reset state and navigate AFTER all async operations complete
  // This ensures reminder creation finishes before component unmounts
  setIsSaved(true);
  setTags([]);
  setUploadedFiles([]);
  setUploadedDocuments([]);
  setReminders([]);
  setShowReminderSection("no");
  
  if (typeof onSuccess === "function") {
    onSuccess();
  } else {
    navigate("/my-liability");
  }
} catch (err) {
  if (err?.response) {
    console.error("err.response.status:", err.response.status);
    console.error("err.response.data:", err.response.data);
    // showErrorToast(err.response.data?.message || "Failed to submit liability");
  } else {
    showErrorToast("Failed to submit liability: " + (err?.message || "Unknown error"));
  }
} finally {
  if (formikHelpers) {
    formikHelpers.setSubmitting(false);
  }
}



  }

  // Step navigation
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  // Step 3 fields (reminder, notes, tags) are checked on Save, not when leaving step 2.
  const STEP3_FIELDS = ["setReminder", "setReminderToggle", "reminderDescription", "reminderType", "reminderDay", "reminderMonth", "notes", "tags"]

  // Mobile Next: validate step 2 with the same schema Save uses, and stay on the step if it has errors (ML-04).
  const handleNextStep = async (values, { setTouched, setErrors, touched }) => {
    if (currentStep === 2) {
      try {
        await getValidationSchema(values.liability).validate(values, { abortEarly: false })
      } catch (err) {
        const stepErrors = {}
        ;(err.inner || []).forEach((e) => {
          if (e.path && !STEP3_FIELDS.includes(e.path.split(".")[0]) && !stepErrors[e.path]) {
            stepErrors[e.path] = e.message
          }
        })
        if (Object.keys(stepErrors).length > 0) {
          const stepTouched = Object.keys(stepErrors).reduce((acc, path) => ({ ...acc, [path]: true }), {})
          setTouched({ ...touched, ...stepTouched }, false)
          setErrors(stepErrors)
          showErrorToast("Please fix the highlighted fields before continuing.")
          setTimeout(() => {
            const firstInvalid = document.querySelector(".is-invalid, .invalid-feedback, .text-danger.small")
            if (firstInvalid) firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
          }, 0)
          return
        }
      }
    }
    nextStep()
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Validate current step
  const validateStep = (values, errors) => {
    switch (currentStep) {
      case 1:
        return true
      case 2:
        return true
      case 3:
        return !errors.setReminder
      default:
        return true
    }
  }
function touchedFromYupErrors(yupErr) {
  const touched = {};
  if (yupErr && Array.isArray(yupErr.inner)) {
    yupErr.inner.forEach(e => {
      if (e.path) touched[e.path] = true;
    });
  }
  return touched;
}
  // Render liability form based on selection
  const renderliabilityForm = (values, setFieldValue, errors, touched, handleChange) => {
    switch (values.liability) {
      case "mortgage":
        return <MortgageForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "vehicle loan":
        return <VehicleForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "loan":
        return <LoanForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "credit card":
        return <CreditCardForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "overdraft":
        return <OverdraftForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "tax payable":
        return <TaxPayableForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "bnpl":
        return <BNPLForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "insurance premium":
        return <InsurancePremiumForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      case "personal debts":
        return <PersonalDebtsForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} handleChange={handleChange} localReadOnly={localReadOnly} />
      default:
        return <div className="text-muted">Please select a Liability to continue</div>
    }
  }

  // Render step content
  const renderStepContent = (values, setFieldValue, errors, touched, handleChange) => {
    if (!isMobile) {
      // Desktop view - show all content (layout aligned with AddAssets.js)
      return (
        <>
          <div className="row">
            <div className="col-12">
              {/* Header with Edit button for existing liabilities in view mode */}
              {liabilityId && localReadOnly && (
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4>View Liability</h4>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setLocalReadOnly(false);
                      if (typeof enterEditMode === "function") enterEditMode();
                    }}
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                </div>
              )}
              {liabilityId && !localReadOnly && (
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4>Edit Liability</h4>
                </div>
              )}
              {!liabilityId && (
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4>Add Liability</h4>
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 col-md-12">
              {renderStep1Content(values, setFieldValue, errors, touched)}
            </div>
            <div className="col-lg-8 col-md-12">
              {renderStep2Content(values, setFieldValue, errors, touched, handleChange)}
            </div>
            <div className="col-lg-4 col-md-12 ms-auto right-col">
              {renderStep3Content(values, setFieldValue, errors, touched)}
            </div>
          </div>
        </>
      )
    } else {
    // Mobile view - show step by step
    return <div>
      {/* Header with Edit button for existing liabilities in view mode (mobile) */}
      {liabilityId && localReadOnly && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>View Liability</h4>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setLocalReadOnly(false);
              if (typeof enterEditMode === "function") enterEditMode();
            }}
          >
            <i className="bi bi-pencil"></i> Edit
          </button>
        </div>
      )}
      {liabilityId && !localReadOnly && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Edit Liability</h4>
        </div>
      )}
      {!liabilityId && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Add Liability</h4>
        </div>
      )}
      {(() => {
        switch (currentStep) {
          case 1: return renderStep1Content(values, setFieldValue, errors, touched)
          case 2: return renderStep2Content(values, setFieldValue, errors, touched, handleChange)
          case 3: return renderStep3Content(values, setFieldValue, errors, touched)
          default: return null
        }
      })()}
    </div>
    }
  }

  // Step 1: Document Upload - Only show for fresh Add Liability (not edit/view mode)
  const renderStep1Content = (values, setFieldValue, errors, touched, resetForm) => (
    <>
      {/* AI Extraction Upload Section - Only for new liability */}
      {!liabilityId && !apiInitialValues && (
        <>
          {/* Premium Upload Area with drag and drop - WRAPPER */}
          {extractLoading && <Loader />}
          {/* {extractMessage && <div className="alert alert-warning mt-2 text-danger">{extractMessage}</div>} */}
          <div
            {...getDocRootProps()}
            className={`upload-document-area upload-document-area-liability ${fileUploading ? "uploading" : ""} ${isDocDragActive ? "drag-active" : ""}`}
          >
        {/* Floating particles */}
        <div className="particles">
          <div className="particle p1" />
          <div className="particle p2" />
          <div className="particle p3" />
          <div className="particle p4" />
        </div>
        {/* Sparkle decorations */}
        <div className="upload-sparkles">
          <svg className="upload-sparkle s1" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
          </svg>
          <svg className="upload-sparkle s2" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
          </svg>
          <svg className="upload-sparkle s3" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
          </svg>
        </div>

        <div
          className="w-100 h-100 d-flex flex-column justify-content-center align-items-center position-relative"
          style={{ cursor: "pointer", minHeight: "120px" }}
          onClick={() => openDocInput()}
        >
          {/* Animated icon */}
          <div className="d-flex justify-content-center align-items-center gap-3">
            <div>
              <div className="icon-container">
                <div className="icon-glow" />
                <div className="icon-bg" />
                <svg className="upload-sign" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 16V4M12 4L8 8M12 4L16 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 16V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
            <div className="d-flex flex-col">
              <span className="fw-semibold">
                {isDocDragActive ? "Drop your document here" : "Upload your document"}
              </span>
              <small className="text-muted">
                Drag & drop or <span style={{ color: "#3b82f6", fontWeight: 500 }}>click to browse</span>
              </small>
              <small className="text-muted mt-2" style={{ fontSize: "11px" }}>
                PDF, PNG, JPG, DOCX • Max 10MB
              </small>
            </div>
          </div>
          {/* AI Badge */}
          <div className="ai-badge-effect">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
            </svg>
            <span>AI-powered extraction</span>
          </div>
        </div>

        <input
          key={docInputKey}
          ref={docInputRef}
          {...getDocInputProps()}
          id="ai-doc-upload-step1"
          onClick={e => {
            // Always reset value before opening dialog
            e.target.value = '';
            if (getDocInputProps().onClick) getDocInputProps().onClick(e);
          }}
        />
      </div>

      {/* Display uploaded documents if any */}
      {uploadedDocuments.length > 0 && (
        <div className="mb-3 flex">
          {uploadedDocuments.map((doc, index) => (
            <div key={doc.id} className="uploaded-file-item">
              <div className="file-preview">
                <i className="bi bi-file-earmark-pdf"></i>
              </div>
              <div className="file-info">
                <span className="file-number">{index + 1}. </span>
                <span className="file-name">{doc.name}</span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-danger remove-file"
                onClick={() => removeDocument(doc.id)}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="upload-divider-container">
        <hr />
        <span className="text-muted">OR ENTER MANUALLY</span>
      </div>

      {/* Liability Selection */}
      <div className="row">
        <div className="col-12 col-lg-4 mb-4">
          <label className="form-label">
            Liability<span className="text-danger">*</span>
          </label>
          <Select
            options={liabilityOptions}
            value={liabilityOptions.find((option) => option.value === (values.liability || selectedLiability))}
            onChange={(option) => {
              setFieldValue("liability", option.value);
              setSelectedLiability(option.value);
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            isDisabled={localReadOnly}
          />
          <ErrorMessage name="liability" component="div" className="text-danger small" />
        </div>
      </div>
        </>
      )}
    </>
  )

  // Step 2: Dynamic Liability Details
  const renderStep2Content = (values, setFieldValue, errors, touched, handleChange) => (
    <>{renderliabilityForm(values, setFieldValue, errors, touched, handleChange)}</>
  )

  // Step 3: Additional Details
  const renderStep3Content = (values, setFieldValue, errors, touched) => (
    <>
    {/* Set Reminder Section */}
      <SetReminderSection
        showReminderSection={showReminderSection}
        setShowReminderSection={setShowReminderSection}
        reminderForm={reminderForm}
        setReminderForm={setReminderForm}
        reminders={reminders}
        remindersCollapsed={remindersCollapsed}
        setRemindersCollapsed={setRemindersCollapsed}
        editingReminderId={editingReminderId}
        handleAddReminder={handleAddReminder}
        handleEditReminder={handleEditReminder}
        handleDeleteReminder={handleDeleteReminder}
        handleCompleteReminder={handleCompleteReminder}
        getMonthName={getMonthName}
        userId={user?.id}
        liabilityId={liabilityId}
        readOnly={localReadOnly}
        showList={!!liabilityId}
      />
      {/* Notes */}
      <div className="col-12 mb-4">
        <label className="form-label">
          Notes
        </label>
        <textarea
          name="notes"
          className="form-control"
          rows={3}
          placeholder="E.g., Payment details, contact information, special conditions..."
          value={values.notes || ''}
          onChange={(e) => setFieldValue("notes", e.target.value)}
          disabled={localReadOnly}
        />
        {errors.notes && touched.notes && (
          <div className="text-danger small">{errors.notes}</div>
        )}
      </div>

      {/* Tags */}
      <div className="col-12 mb-4 tags-field">
        <label className="form-label">Tags</label>
        <div className="tags-input-container">
          <div className="tag-input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add tag and press enter or click add button"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleTagInput}
              disabled={localReadOnly}
            />
            <button type="button" className="btn btn-add-tag" onClick={addTag} disabled={localReadOnly}>
              Add
            </button>
          </div>
          <div className="tags-display">
            {tags.map((tag, index) => (
              <span key={index} className="tag-item">
                {tag}
                {!localReadOnly && (
                  <button type="button" className="tag-remove" onClick={() => removeTag(tag)}>
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>


      {/* Upload Photos or Documents */}
      <div className="col-12 mb-4">
        <label className="form-label mb-3">Upload Photos or Documents</label>
        <div
          {...getFilesRootProps()}
          className={`upload-file-dropzone ${isFilesDragActive ? "drag-active" : ""} ${localReadOnly ? "disabled" : ""}`}
          style={{ opacity: localReadOnly ? 0.5 : 1, pointerEvents: localReadOnly ? "none" : "auto" }}
        >
          <input {...getFilesInputProps()} />
          <i className="bi bi-plus-lg"></i>
        </div>
        <div className="uploaded-files-list">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.id || file.url || file.originalName || index}
              className="uploaded-file-item mt-4"
            >
              <div className="file-preview">
                {file.preview || (file.url && file.type && file.type.startsWith("image/")) || (file.url && file.url.match(/\.(jpg|jpeg|png|gif)$/i)) ? (
                  <img
                    src={file.preview || file.url}
                    alt={file.name || file.originalName}
                    className="file-image"
                  />
                ) : (
                  <i className="bi bi-file-earmark-text file-icon"></i>
                )}
              </div>
              <div className="file-info">
                <span className="file-number">{index + 1}. </span>
                <span className="file-name">
                  {file.name || file.originalName}
                </span>
              </div>
              {!localReadOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger remove-file"
                  onClick={() =>
                    removeFile(file.id || file.url || file.originalName)
                  }
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
          ))}
          {/* Display uploaded documents */}
          {uploadedDocuments.map((doc, index) => (
            <div
              key={doc.id || doc.url || doc.originalName || `doc-${index}`}
              className="uploaded-file-item mt-4"
            >
              <div className="file-preview">
                <i className="bi bi-file-earmark-text file-icon"></i>
              </div>
              <div className="file-info">
                <span className="file-number">{uploadedFiles.length + index + 1}. </span>
                <span className="file-name">
                  {doc.name || doc.originalName}
                </span>
              </div>
              {!localReadOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger remove-file"
                  onClick={() =>
                    removeDocument(doc.id || doc.url || doc.originalName)
                  }
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )

  // Prepare Formik initial values: prefer apiInitialValues mapped; otherwise dynamic defaults
 const userCurrency = user?.defaultCurrency || user?.currency;
 const computedInitialValues = React.useMemo(() => {
   const dynamic = getDynamicInitialValues(selectedLiability) || {};
   const baseValues = {
     liability: selectedLiability,
     setReminderToggle: "no",
     reminderDescription: '',
     reminderType: 'Monthly',
     reminderDay: '1',
     reminderMonth: '1',
     notes: '',
     ...dynamic,
     // Defaults go after ...dynamic: forms whose initial values hold currency: "" would otherwise
     // overwrite them. New liabilities start in the user's currency (ML-20); the mortgage form
     // keeps its currency in originalLoanCurrency.
     currency: dynamic.currency || userCurrency || "USD",
     originalLoanCurrency: dynamic.originalLoanCurrency || userCurrency || "",
   };
  
  if (apiInitialValues && Object.keys(apiInitialValues).length) {
    return {
      ...baseValues,
      ...apiInitialValues,
      currency: apiInitialValues.currency || dynamic.currency || userCurrency || "USD",
      setReminderToggle: apiInitialValues.setReminderToggle || "no",
      notes: apiInitialValues.notes || "",
    };
  }

  return baseValues;
}, [apiInitialValues, selectedLiability, userCurrency]);


  return (
    <>
      <Layout>
        <div className="form-page-container">
        <div className="inner-container">
              <Formik
                initialValues={computedInitialValues}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={getValidationSchema(selectedLiability)}
                validate={async (values) => {
                  try {
                    await addLiabilitySchema.validate(values, { abortEarly: false });
                    return {};
                  } catch (err) {
                    const errors = {};
                    if (err.inner && Array.isArray(err.inner)) {
                      err.inner.forEach((e) => {
                        if (e.path) errors[e.path] = e.message;
                      });
                    } else if (err.path) {
                      errors[err.path] = err.message;
                    }
                    return errors;
                  }
                }}
                onSubmit={async (values, formikHelpers) => {
  // --- mark all nested fields as touched (so validation messages appear for nested paths) ---
  const allTouched = buildTouchedFromInitial(formikHelpers.initialValues || {});
  formikHelpers.setTouched(allTouched, false);

  // select correct schema for the selected liability (same as your existing switch)
  let schema;
  switch (values?.liability) {
    case "mortgage":
      schema = mergeSchemas(addLiabilitySchema, loanFormValidationSchema);
      break;
    case "vehicle loan":
      schema = mergeSchemas(addLiabilitySchema, vehicleValidationSchema);
      break;
    case "loan":
      schema = mergeSchemas(addLiabilitySchema, cashValidationSchema);
      break;
    case "credit card":
      schema = mergeSchemas(addLiabilitySchema, creditCardValidationSchema);
      break;
    case "overdraft":
      schema = mergeSchemas(addLiabilitySchema, overdraftValidationSchema);
      break;
    case "tax payable":
      schema = mergeSchemas(addLiabilitySchema, taxValidationSchema);
      break;
    case "insurance premium":
      schema = mergeSchemas(addLiabilitySchema, insuranceValidationSchema);
      break;
    case "personal debts":
      schema = mergeSchemas(addLiabilitySchema, liabilityValidationSchema);
      break;
    case "bnpl":
      schema = mergeSchemas(addLiabilitySchema, bnplValidationSchema);
      break;
    default:
      schema = addLiabilitySchema;
  }

  try {
    await schema.validate(values, { abortEarly: false });

    // if the form is in readOnly mode, don't submit
    if (localReadOnly) {
      if (formikHelpers) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }

    // proceed with your internal submit (handles file uploads + parent delegation)
    await internalSubmit(values, formikHelpers);
  } catch (err) {
    // build errors object from Yup
    const errors = {};
    if (err && Array.isArray(err.inner)) {
      err.inner.forEach(e => {
        if (e.path) errors[e.path] = e.message;
      });
    } else if (err && err.path) {
      errors[err.path] = err.message;
    }
    formikHelpers.setErrors(errors);

    // also ensure all errored fields are touched (so ErrorMessage and field-level UI show)
    const touchedFromErrors = touchedFromYupErrors(err);
    formikHelpers.setTouched({
      ...allTouched,
      ...touchedFromErrors
    }, false);

    // Tell the user why nothing was saved; on mobile, go back to step 2 when its fields have errors (ML-12).
    if (Object.keys(errors).length > 0) {
      const hasStep2Errors = Object.keys(errors).some(path => !STEP3_FIELDS.includes(path.split(".")[0]));
      if (isMobile && hasStep2Errors && currentStep !== 2) {
        setCurrentStep(2);
      }
      showErrorToast("Please fix the highlighted fields before saving.");
    }

    formikHelpers.setSubmitting(false);
  }
}}

              >
                {({ values, setFieldValue, errors, touched, isSubmitting, handleChange, setTouched, setErrors }) => {
                  // Capture setFieldValue for AI extraction (direct assignment, no useEffect needed in callback)
                  setFieldValueRef.current = setFieldValue;

                  return (
                    <Form>
                      {/* AI Extraction Loading & Message */}
                      {(extractLoading || extractMessage) && (
                        <div className={`alert ${extractLoading ? 'alert-info' : 'alert-success'} mb-3`} role="alert">
                          {extractLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Uploading document and extracting data...
                            </>
                          ) : (
                            extractMessage
                          )}
                        </div>
                      )}
                    {renderStepContent(values, setFieldValue, errors, touched, handleChange)}

                    {/* Action Buttons */}
                    <div className="action-buttons">
                      {isMobile ? (
                        // Mobile step navigation
                        <>
                          {(currentStep > 1 || currentStep === 1) && (
                            <div className="mobile-nav-buttons">
                              <button type="button" className="btn btn-outline-secondary" onClick={currentStep > 1 ? prevStep : () => navigate('/my-liability')}>
                                <i className="bi bi-arrow-left"></i> Back
                              </button>
                            </div>
                          )}
                          <div className="mobile-nav-buttons">
                            {currentStep < 3 ? (
                              <>
                                {/* Next now blocks invalid details, so offer Save as Draft here too:
                                    an unfinished liability can still be saved from step 2 on mobile. */}
                                {currentStep === 2 && !localReadOnly && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary me-3"
                                    onClick={() => handleSaveAsDraft(values)}
                                    disabled={isSubmitting}
                                  >
                                    <i className="bi bi-pencil"></i> Save as Draft
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => handleNextStep(values, { setTouched, setErrors, touched })}
                                  disabled={!validateStep(values, errors)}
                                >
                                  Next <i className="bi bi-arrow-right"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                {!localReadOnly && (
                                  <>
                                     <button
                                       type="button"
                                       className="btn btn-outline-primary me-3"
                                       onClick={() => handleSaveAsDraft(values)}
                                       disabled={isSubmitting}
                                     >
                                       <i className="bi bi-pencil"></i> Save as Draft
                                     </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                      {isSubmitting ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      ) : (
                                        <i className="bi bi-floppy"></i>
                                      )}
                                      {isSubmitting ? "Saving..." : "Save Liability"}
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        // Desktop navigation
                        <>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/my-liability')}>
                            <i className="bi bi-arrow-left"></i> Back
                          </button>
                          <div className="action-buttons-right">
                            {!localReadOnly && (
                              <>
                                 <button
                                   type="button"
                                   className="btn btn-outline-primary me-3"
                                   onClick={() => handleSaveAsDraft(values)}
                                   disabled={isSubmitting}
                                 >
                                   <i className="bi bi-pencil"></i> Save as Draft
                                 </button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                  {isSubmitting ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  ) : (
                                    <i className="bi bi-floppy"></i>
                                  )}
                                  {isSubmitting ? "Saving..." : "Save Liability"}
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </Form>
                )}
              }
              </Formik>
        </div>
      </div>
    </Layout>
    </>
  )
}

export default AddLiability
